import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import ConfirmModal from '@components/modal/Confirm.modal';
import NewTableComponent from '@components/table/NewTable.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import {Box, Stack} from '@mui/material';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {useObservable} from '@ngneat/react-rxjs';
import {Client} from '@store/clients';
import {Document, documentsQuery, documentsService, ManageDocument} from '@store/documents';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {finalize, Observable} from 'rxjs';

const ExecutionDocument = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);


  const [{ documents, loading }] = useObservable(documentsQuery.documents$);
  const [filters] = useObservable(documentsQuery.filters$);
  const [sortModel] = useObservable(documentsQuery.sort$);
  const [tablePagination] = useObservable(documentsQuery.pagination$);

  const handleConfirmValidation = (type: OtherActionType, client: Client) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = documentsService.deleteDocument(client.id);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.document`), { variant: 'success' });
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} width="100%" spacing={2} mt="25px !important">
      <Stack flex={1} spacing={2} pb="25px">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box>
            <DatePickerComponent
              size="small"
              inputWidth={175}
              placeholder={t('documents.filters.importDate')}
              value={filters.importDate}
              handleChange={documentsService.setFilters('importDate')} />
          </Box>
          <TableFiltersComponent
            filters={filters}
            handleSave={documentsService.setFilters('tableFilters')}
            handleReset={() => documentsService.resetFilters()} />
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT({
              field: 'document.issue',
              label: t('operations.executions.table.createdAt'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Document>) => params.row.createdAt,
              valueFormatter: (params) => (params.value ? t('global.dateAndHour', { date: new Date(params.value) }) : ''),
              minWidth: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'document.author',
              label: t('documents.table.author'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<ManageDocument, ManageDocument>) => params.row.author,
              minWidth: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'document.execution.name',
              label: t('invoices.executions.table.name'),
              fullWidth: true,
              minWidth: 350,
              valueGetter: (params: GridValueGetterParams<ManageDocument, ManageDocument>) => params.row.execution?.name,
            }),
            COLUMN_TYPES.TEXT({
              field: 'document.execution.reference',
              label: t('operations.nonCompliance.table.ticket'),
              valueGetter: (params: GridValueGetterParams<ManageDocument, ManageDocument>) => params.row.execution?.ticketSignature,
              width: 200,
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (document: Document) => navigate(`/operations/executions/${document.execution.id}`),
            }),
          ]}
          disableRowSelection
          handlePageSizeChange={documentsService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => documentsService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={documentsService.setSort}
          sortModel={[sortModel]}
          totalRows={documentsQuery.totalRows}
          data={documents} />
        {!!openConfirmAction && (
          <ConfirmModal
            handleClose={() => setOpenConfirmAction(false)}
            description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.document`)}
            handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
            title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
            validateButton={OtherActionType.label(openConfirmAction.type)} />
        )}
        <Outlet />
      </Stack>

    </Stack>
  );
};

export default ExecutionDocument;
